import { useState, useEffect } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  UserPlus, Check, X, Clock, Calendar, Phone, Mail, 
  MapPin, FileText, User, Heart, Trash2, Copy, Link2, ExternalLink
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function PreMatriculas({ onLogout }) {
  const [preMatriculas, setPreMatriculas] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [escolaCodigo, setEscolaCodigo] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pendentes");
  const [selectedPreMatricula, setSelectedPreMatricula] = useState(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  const [approveData, setApproveData] = useState({
    turma_id: "",
    valor_mensalidade: "",
    dia_vencimento: "10"
  });
  
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [preMatriculasRes, turmasRes, codigoRes] = await Promise.all([
        api.get('/pre-matriculas'),
        api.get('/turmas'),
        api.get('/arena/escola/codigo')
      ]);
      setPreMatriculas(preMatriculasRes.data);
      setTurmas(turmasRes.data);
      setEscolaCodigo(codigoRes.data.codigo);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredPreMatriculas = () => {
    switch (activeTab) {
      case "pendentes":
        return preMatriculas.filter(p => p.status === "pendente");
      case "aprovadas":
        return preMatriculas.filter(p => p.status === "aprovada");
      case "rejeitadas":
        return preMatriculas.filter(p => p.status === "rejeitada");
      default:
        return preMatriculas;
    }
  };

  const handleApprove = async () => {
    if (!approveData.turma_id || !approveData.valor_mensalidade) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    
    try {
      await api.post(`/pre-matriculas/${selectedPreMatricula.id}/aprovar`, {
        turma_id: approveData.turma_id,
        valor_mensalidade: parseFloat(approveData.valor_mensalidade),
        dia_vencimento: parseInt(approveData.dia_vencimento)
      });
      toast.success("Pré-matrícula aprovada! Aluno cadastrado com sucesso.");
      setShowApproveDialog(false);
      setSelectedPreMatricula(null);
      setApproveData({ turma_id: "", valor_mensalidade: "", dia_vencimento: "10" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao aprovar pré-matrícula");
    }
  };

  const handleReject = async () => {
    try {
      await api.post(`/pre-matriculas/${selectedPreMatricula.id}/rejeitar`, {
        motivo: rejectReason
      });
      toast.success("Pré-matrícula rejeitada");
      setShowRejectDialog(false);
      setSelectedPreMatricula(null);
      setRejectReason("");
      fetchData();
    } catch (error) {
      toast.error("Erro ao rejeitar pré-matrícula");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/pre-matriculas/${deleteId}`);
      toast.success("Pré-matrícula deletada");
      setDeleteId(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao deletar pré-matrícula");
    }
  };

  const copyLink = async () => {
    const link = `${window.location.origin}/matricula/${escolaCodigo}`;
    
    try {
      // Tenta usar a API moderna de clipboard
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
        toast.success("Link copiado para a área de transferência!");
      } else {
        // Fallback para navegadores/contextos que não suportam clipboard API
        const textArea = document.createElement("textarea");
        textArea.value = link;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          toast.success("Link copiado para a área de transferência!");
        } else {
          toast.error("Não foi possível copiar. Copie manualmente: " + link);
        }
      }
    } catch (err) {
      // Fallback final
      const textArea = document.createElement("textarea");
      textArea.value = link;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          toast.success("Link copiado para a área de transferência!");
        } else {
          toast.info(`Copie o link: ${link}`);
        }
      } catch (e) {
        toast.info(`Copie o link: ${link}`);
      }
      document.body.removeChild(textArea);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const pendentesCount = preMatriculas.filter(p => p.status === "pendente").length;
  const aprovadasCount = preMatriculas.filter(p => p.status === "aprovada").length;
  const rejeitadasCount = preMatriculas.filter(p => p.status === "rejeitada").length;

  if (loading) {
    return <Layout onLogout={onLogout}><div className="text-center py-12">Carregando...</div></Layout>;
  }

  return (
    <Layout onLogout={onLogout}>
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2" style={{ fontFamily: 'Space Grotesk' }}>Pré-Matrículas</h1>
            <p className="text-sm sm:text-base text-gray-600">Gerencie as solicitações de matrícula</p>
          </div>
          
          {/* Link de Pré-Matrícula */}
          <Card className="w-full">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-2">
                <Link2 className="w-4 h-4 text-purple-600 flex-shrink-0" />
                <span className="text-sm font-medium">Link de Pré-Matrícula</span>
              </div>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-gray-100 px-2 py-1.5 rounded flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                  /matricula/{escolaCodigo}
                </code>
                <Button size="sm" variant="outline" onClick={copyLink} className="flex-shrink-0">
                  <Copy className="w-4 h-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="flex-shrink-0"
                  onClick={() => window.open(`/matricula/${escolaCodigo}`, '_blank')}
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-auto min-w-full sm:grid sm:w-full sm:grid-cols-3 gap-1">
              <TabsTrigger value="pendentes" className="flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-4 whitespace-nowrap">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden xs:inline">Pendentes</span>
                <span className="xs:hidden">Pend.</span>
                {pendentesCount > 0 && (
                  <Badge className="ml-1 sm:ml-2 bg-amber-500 text-xs px-1.5">{pendentesCount}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="aprovadas" className="flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-4 whitespace-nowrap">
                <Check className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden xs:inline">Aprovadas</span>
                <span className="xs:hidden">Aprov.</span>
                {aprovadasCount > 0 && (
                  <Badge className="ml-1 sm:ml-2 bg-green-500 text-xs px-1.5">{aprovadasCount}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="rejeitadas" className="flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-4 whitespace-nowrap">
                <X className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden xs:inline">Rejeitadas</span>
                <span className="xs:hidden">Rejeit.</span>
                {rejeitadasCount > 0 && (
                  <Badge className="ml-1 sm:ml-2 bg-red-500 text-xs px-1.5">{rejeitadasCount}</Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value={activeTab} className="mt-4 sm:mt-6">
            {getFilteredPreMatriculas().length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  {activeTab === "pendentes" 
                    ? "Nenhuma pré-matrícula pendente"
                    : activeTab === "aprovadas"
                    ? "Nenhuma pré-matrícula aprovada"
                    : "Nenhuma pré-matrícula rejeitada"}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {getFilteredPreMatriculas().map((pm) => (
                  <Card key={pm.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col gap-3 sm:gap-4">
                        {/* Dados do Aluno */}
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                            <h3 className="font-semibold text-base sm:text-lg">{pm.aluno_nome}</h3>
                            <Badge variant={pm.status === "pendente" ? "outline" : pm.status === "aprovada" ? "default" : "destructive"} className="text-xs">
                              {pm.status === "pendente" ? "Pendente" : pm.status === "aprovada" ? "Aprovada" : "Rejeitada"}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-1 gap-1 sm:gap-2 text-xs sm:text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                              <span>Nascimento: {formatDate(pm.aluno_data_nascimento)}</span>
                            </div>
                            {pm.aluno_endereco && (
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                <span className="truncate">{pm.aluno_endereco}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Dados do Responsável */}
                          <div className="mt-3 p-2.5 sm:p-3 bg-gray-50 rounded-lg">
                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                              <Heart className="w-3 h-3 sm:w-4 sm:h-4 text-pink-600 flex-shrink-0" />
                              <span className="font-medium text-sm sm:text-base">{pm.responsavel?.nome}</span>
                              <Badge variant="outline" className="text-xs px-1.5">
                                {pm.responsavel?.grau_parentesco}
                              </Badge>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-4 text-xs sm:text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                <span>{pm.responsavel?.telefone}</span>
                              </div>
                              {pm.responsavel?.email && (
                                <div className="flex items-center gap-1">
                                  <Mail className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                  <span className="truncate">{pm.responsavel?.email}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Info adicional */}
                          <div className="mt-2 sm:mt-3 text-xs text-gray-500 space-y-0.5">
                            <div className="flex items-center gap-1">
                              <FileText className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">Assinado por: {pm.assinatura_nome}</span>
                            </div>
                            <span>Enviado em: {formatDateTime(pm.created_at)}</span>
                          </div>
                        </div>

                        {/* Ações */}
                        <div className="flex flex-row gap-2 justify-between sm:justify-end border-t pt-3 sm:border-0 sm:pt-0">
                          {pm.status === "pendente" && (
                            <>
                              <Button 
                                className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 text-sm"
                                size="sm"
                                onClick={() => {
                                  setSelectedPreMatricula(pm);
                                  setShowApproveDialog(true);
                                }}
                              >
                                <Check className="w-4 h-4 mr-1 sm:mr-2" />
                                Aprovar
                              </Button>
                              <Button 
                                variant="outline"
                                size="sm"
                                className="flex-1 sm:flex-none text-red-600 border-red-300 hover:bg-red-50 text-sm"
                                onClick={() => {
                                  setSelectedPreMatricula(pm);
                                  setShowRejectDialog(true);
                                }}
                              >
                                <X className="w-4 h-4 mr-1 sm:mr-2" />
                                Rejeitar
                              </Button>
                            </>
                          )}
                          <Button 
                            variant="ghost"
                            size="sm"
                            className="text-gray-500 px-2"
                            onClick={() => setDeleteId(pm.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog de Aprovação */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aprovar Pré-Matrícula</DialogTitle>
            <DialogDescription>
              Complete os dados para finalizar a matrícula de <strong>{selectedPreMatricula?.aluno_nome}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Turma *</Label>
              <Select
                value={approveData.turma_id}
                onValueChange={(value) => setApproveData({ ...approveData, turma_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a turma" />
                </SelectTrigger>
                <SelectContent>
                  {turmas.map((turma) => (
                    <SelectItem key={turma.id} value={turma.id}>
                      {turma.nome} - {turma.modalidade}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Valor da Mensalidade *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={approveData.valor_mensalidade}
                  onChange={(e) => setApproveData({ ...approveData, valor_mensalidade: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Dia de Vencimento *</Label>
                <Select
                  value={approveData.dia_vencimento}
                  onValueChange={(value) => setApproveData({ ...approveData, dia_vencimento: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 5, 10, 15, 20, 25].map((dia) => (
                      <SelectItem key={dia} value={dia.toString()}>
                        Dia {dia}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Cancelar
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleApprove}>
              Aprovar e Cadastrar Aluno
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Rejeição */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Pré-Matrícula</DialogTitle>
            <DialogDescription>
              Informe o motivo da rejeição (opcional)
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Input
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Motivo da rejeição..."
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleReject}>
              Rejeitar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta pré-matrícula? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
